import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { PatientModel } from '../../core/api/models';
import { OfflineDataService, PatientOfflineData } from '../../core/services/offline-data.service';
import { PatientSessionService } from '../../staff/home/patient-session.service';
import { AuthService } from '../../auth/auth.service';
import { Permissions } from '../../core/api/models';
@Component({
  selector: 'app-offline-patients',
  templateUrl: './offline-patients.component.html',
  styleUrls: ['./offline-patients.component.css'],
  standalone: false,
})
export class OfflinePatientsComponent implements OnInit {
  patients: PatientOfflineData[] = [];
  loading: boolean = true;
  errorMessage: string = '';
  redownloading: { [key: number]: boolean } = {};

  constructor(
    private router: Router,
    protected offlineDataService: OfflineDataService,
    private patientSessionService: PatientSessionService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.loadOfflinePatients();
  }

  loadOfflinePatients(): void {
    this.loading = true;
    this.errorMessage = '';
    this.offlineDataService.getOfflinePatients().subscribe({
      next: (patients) => {
        if (patients) {
          this.patients = Array.from(patients.values()).map(data => data);
        }
        this.loading = false;

        if (this.patients.length === 0) {
          this.errorMessage = 'No offline patient data available.';
        } else {
          this.errorMessage = '';
        }

        //remove sessions that have a StaffID and is not the current user
        this.patients.forEach(patient => {
          patient.sessions = patient.sessions.filter(session => (!session.StaffID || session.StaffID === this.authService.userInfo?.ID || session.StaffIDs?.includes(this.authService.userInfo?.ID ?? 0)) && (session.ID ||0) >=0);
        });

        this.patients.sort((a, b) => {
          return new Date(b.lastSyncTime).getTime() - new Date(a.lastSyncTime).getTime();
        });
      },
      error: (error) => {
        console.error('Error loading offline patients', error);
        this.errorMessage = 'Error loading offline patients.';
        this.loading = false;
      }
    });
  }

  isOffline(): boolean {
    return this.offlineDataService.isInOfflineMode();
  }

  redownloadPatientData(patient: PatientModel): void {
    if (!patient.ID) return;

    const patientId = patient.ID;
    this.redownloading[patientId] = true;
    this.errorMessage = '';

    this.offlineDataService.savePatientForOffline(patientId).subscribe({
      next: (success) => {
        if (success) {
          // Reload the patients list to show updated data
          this.loadOfflinePatients();
        } else {
          this.errorMessage = 'Failed to re-download patient data.';
        }
        this.redownloading[patientId] = false;
      },
      error: (error) => {
        console.error('Error re-downloading patient data', error);
        this.errorMessage = 'Error re-downloading patient data.';
        this.redownloading[patientId] = false;
      }
    });
  }

  selectPatient(patient: PatientModel): void {
    if (patient.ID) {
      this.router.navigate(['/offline/patient', patient.ID]);
    } else {
      this.errorMessage = 'Invalid patient data.';
    }
  }

  tryReconnect(): void {
    this.offlineDataService.checkIsOnline().subscribe((isOnline) => {
      if (isOnline) {
        this.navigateBackToOnline();
        return;
      }

      this.errorMessage = 'Still offline. Please check your connection.';
    });
  }

  removeAllWithoutUnsynchedData(): void {
    //Get all patients that dont have unsynched data
    const patients = this.patients.filter(patient => !patient.sessions.some(session => (session.isDirty && (session.ID ||0) >=0))
    || (patient.progressNotes && Object.values(patient.progressNotes).some(note => (note as any).isDirty)));
    const patientIds = patients.map(patient => patient.patient.ID).filter(id => id !== undefined) as number[];
    this.offlineDataService.removeBulkOfflinePatientData(patientIds);
  }



  goBackToOnline(): void {
    this.offlineDataService.checkIsOnline().subscribe((isOnline) => {
      if (!isOnline) {
        this.errorMessage = 'Still offline. Please check your connection.';
        return;
      }

      this.navigateBackToOnline();
    });
  }

  private navigateBackToOnline(): void {
    if (this.patientSessionService.getPatientID()) {
      if (this.authService.userInfo?.Permissions?.includes(Permissions.SparkApp_Supervisor)) {
        this.router.navigate(['/supervisor/' + this.patientSessionService.getPatientID() + '/overview']);
      } else {
        this.router.navigate(['/staff/' + this.patientSessionService.getPatientID() + '/sessions']);
      }
    } else {
      this.router.navigate(['/']);
    }
  }

  patientHasOpenSessions(patient: PatientOfflineData): boolean {
    return patient.sessions.some(session => !session.FinishedDate);
  }

  patientHasDirtySessions(patient: PatientOfflineData): boolean {
    return patient.sessions.some(session => (session.isDirty && (session.ID ||0) >=0)
    || (patient.progressNotes && Object.values(patient.progressNotes).some(note => (note as any).isDirty)));
  }
} 
